import { useRef, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import ProductCard from '../chat/ProductComponent'
import FeatureGate from '../FeaturesGate'
import './productSlider.css'

// ─── Azure Blob ───────────────────────────────────────────────────────────────
const SAS_TOKEN =
  'sv=2024-11-04&ss=bfqt&srt=sco&sp=rwdlacupiytfx&se=2027-03-01T20:08:03Z&st=2026-03-25T11:53:03Z&spr=https,http&sig=TygbDO5J47eNr7ajUYXfT2ta4TOYsd2SlWNmFMQsz6U%3D'
const BLOB_BASE =
  'https://halcosgwcblobv1.blob.core.windows.net/halcosgwcproductimages'

async function fetchProductImage(
  productName,
  productCategory,
  productBrand,
  isMounted,
) {
  const encodedBrand = encodeURIComponent(productBrand)
  const encodedProduct = encodeURIComponent(productName)
  const primaryUrl = `${BLOB_BASE}/${encodedBrand}-${encodedProduct}.png?${SAS_TOKEN}`

  try {
    const res = await axios.get(primaryUrl)
    return isMounted ? res.config.url : null
  } catch {
    const encodedCat = encodeURIComponent(productCategory.replace(/'/g, '%27'))
    const fallbackUrl = `${BLOB_BASE}/Placeholder-${encodedCat}.png?${SAS_TOKEN}`
    try {
      const catRes = await axios.get(fallbackUrl)
      return isMounted ? catRes.config.url : null
    } catch {
      return null
    }
  }
}

// ─── CSS ──────────────────────────────────────────────────────────────────────

const CARD_WIDTH = 220
const CARD_GAP = 12
const CARD_STRIDE = CARD_WIDTH + CARD_GAP

/**
 * ProductSlider
 *
 * Props:
 *   title          string    — slider heading
 *   onAdd          function  — called when + is tapped
 *   onOpenDiag     function  — opens the DiagnosticsCard modal
 */
export default function ProductSlider({
  title,
  onAdd,
  onOpenDiag,
  goToPricing,
  selectedProduct,
}) {
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}
  const productRecommendations = useSelector(
    (state) => state.productRecommendations,
  )

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language,
  ) => {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  const translateText = (keyPath, replacements = {}) => {
    let value = getTranslationText(keyPath)
    if (typeof value === 'string') {
      Object.entries(replacements).forEach(([key, replacement]) => {
        value = value.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          String(replacement),
        )
      })
    }
    return value
  }

  const trackRef = useRef(null)
  const [current, setCurrent] = useState(0)
  const [productImages, setProductImages] = useState({})
  const [imagesLoading, setImagesLoading] = useState(false)

  const hasProducts = productRecommendations?.product_name?.length > 0

  // ── Fetch images when products arrive in Redux ─────────────────────────────
  useEffect(() => {
    if (!hasProducts) return

    let isMounted = true
    setImagesLoading(true)

    const fetchAll = async () => {
      const images = {}
      const names = productRecommendations.product_name
      const brands = productRecommendations.product_brand
      const categories = productRecommendations.product_category

      for (let i = 0; i < names.length; i++) {
        const url = await fetchProductImage(
          names[i],
          categories[i],
          brands[i],
          isMounted,
        )
        if (url && isMounted) images[names[i]] = url
      }

      if (isMounted) {
        setProductImages(images)
        setImagesLoading(false)
      }
    }

    fetchAll()
    return () => {
      isMounted = false
    }
  }, [productRecommendations]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build card list ────────────────────────────────────────────────────────
  const displayProducts = hasProducts
    ? productRecommendations.product_name.map((name, i) => ({
        id: i,
        name,
        brand: productRecommendations.product_brand?.[i] ?? '',
        category: productRecommendations.product_category?.[i] ?? '',
        price: parseFloat(productRecommendations.price_filtered?.[i]) || 0,
        quantity: productRecommendations.product_quantity?.[i] ?? '',
        ingredients: productRecommendations.product_ingredients?.[i] ?? [],
        currency: '€',
        badge: translateText('chat.productSlider.recommended'),
        imageUrl: productImages[name] ?? null,
        productUrl: productRecommendations.product_url?.[i] ?? '',
        description: productRecommendations.product_description?.[i] ?? '',
      }))
    : []

  const total = displayProducts.length

  const scrollTo = (index) => {
    const clamped = Math.max(0, Math.min(index, total - 1))
    setCurrent(clamped)
    trackRef.current?.scrollTo({
      left: clamped * CARD_STRIDE,
      behavior: 'smooth',
    })
  }

  const onScroll = () => {
    if (!trackRef.current) return
    const idx = Math.round(trackRef.current.scrollLeft / CARD_STRIDE)
    setCurrent(Math.max(0, Math.min(idx, total - 1)))
  }

  // ── No products yet — just show nothing (Chatbot shows its own loading) ───
  if (!hasProducts) return null

  return (
    <>
      <div className='ps-wrap'>
        {/* Header + Diagnostics button side by side */}
        <div className='ps-diag-row'>
          <div className='ps-title'>{title || ''}</div>
          {onOpenDiag && (
            <FeatureGate
              feature='hair_diagnostics'
              onUpgrade={() => goToPricing()}
            >
              <button className='ps-diag-btn' onClick={onOpenDiag}>
                🔬 {translateText('chat.productSlider.viewDiagnostics')}
              </button>
            </FeatureGate>
          )}
        </div>

        <div
          style={{
            fontSize: '11px',
            color: '#aaa',
            marginBottom: '10px',
            paddingLeft: '2px',
          }}
        >
          {total === 1
            ? translateText('chat.productSlider.productCount', { count: total })
            : translateText('chat.productSlider.productCountPlural', {
                count: total,
              })}
        </div>

        {/* Scrollable track */}
        <div className='ps-track-wrap'>
          <div className='ps-track' ref={trackRef} onScroll={onScroll}>
            {displayProducts.map((product) => {
              const isSelected =
                selectedProduct?.id === product.id ||
                selectedProduct?.name === product.name

              return (
                <div
                  key={product.id}
                  className={`product-card ${isSelected ? 'selected-product-card' : ''}`}
                >
                  <ProductCard
                    name={product.name}
                    brand={product.brand}
                    category={product.category}
                    ingredients={product.ingredients}
                    quantity={product.quantity}
                    price={product.price}
                    currency={product.currency}
                    badge={product.badge}
                    imageUrl={product.imageUrl}
                    imageLoading={imagesLoading && !product.imageUrl}
                    onAdd={() => onAdd?.(product)}
                  />
                </div>
              )
            })}
          </div>
        </div>

        {/* Dots */}
        <div className='ps-dots'>
          {displayProducts.map((_, i) => (
            <button
              key={i}
              className={`ps-dot${i === current ? ' active' : ''}`}
              onClick={() => scrollTo(i)}
            />
          ))}
        </div>

        {/* Arrows */}
        <div className='ps-nav'>
          <button
            className='ps-nav-btn'
            onClick={() => scrollTo(current - 1)}
            disabled={current === 0}
            aria-label='Previous'
          >
            <svg
              width='13'
              height='13'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <polyline points='15 18 9 12 15 6' />
            </svg>
          </button>

          <span className='ps-nav-pill'>
            {current + 1} / {total}
          </span>

          <button
            className='ps-nav-btn'
            onClick={() => scrollTo(current + 1)}
            disabled={current === total - 1}
            aria-label='Next'
          >
            <svg
              width='13'
              height='13'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <polyline points='9 18 15 12 9 6' />
            </svg>
          </button>
        </div>
      </div>
    </>
  )
}
